/**
 * Track Preview Hook
 * Manages "Hook Mode" preview playback for track suggestions.
 * Plays 15-second preview from middle of track using Spotify Connect API.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlayerControls } from '@/api/hooks';

interface UseTrackPreviewOptions {
  onError?: (error: Error) => void;
}

interface UseTrackPreviewReturn {
  playPreview: (track: { id: string; uri: string; durationMs: number }) => void;
  stopPreview: () => void;
  previewTrackId: string | null;
  isPlaying: boolean;
  isPending: boolean;
}

const PREVIEW_DURATION_MS = 15000; // 15 seconds
const HOOK_OFFSET_MS = 7500; // 7.5 seconds before center

/**
 * Hook to manage track preview playback.
 *
 * Plays a 15-second "hook" preview from the middle of a track using Spotify Connect.
 * Auto-stops after 15 seconds and handles cleanup on unmount.
 *
 * @param options - Configuration options
 * @param options.onError - Optional error handler for playback failures
 *
 * @returns Object with playback controls and state
 *
 * @example
 * ```tsx
 * function TrackSuggestion({ track }) {
 *   const { playPreview, stopPreview, previewTrackId, isPlaying, isPending } = useTrackPreview();
 *
 *   return (
 *     <button
 *       onClick={() => playPreview(track)}
 *       disabled={isPending}
 *     >
 *       {previewTrackId === track.id && isPlaying ? 'Stop' : 'Play'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTrackPreview(options?: UseTrackPreviewOptions): UseTrackPreviewReturn {
  const { play, pause } = usePlayerControls();

  // Track currently playing preview
  const [previewTrackId, setPreviewTrackId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Timer ref for auto-stop
  const timerRef = useRef<number | null>(null);

  /**
   * Clear the auto-stop timer
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Stop the current preview playback
   */
  const stopPreview = useCallback(() => {
    clearTimer();
    pause.mutate(undefined, {
      onError: (error: any) => {
        console.error('Failed to stop preview:', error);
        options?.onError?.(error);
      },
    });
    setPreviewTrackId(null);
    setIsPlaying(false);
  }, [clearTimer, pause, options]);

  /**
   * Play a track preview from the middle (hook position)
   *
   * @param track - Track to preview
   * @param track.id - Track ID for state tracking
   * @param track.uri - Spotify URI for playback
   * @param track.durationMs - Track duration in milliseconds
   */
  const playPreview = useCallback(
    (track: { id: string; uri: string; durationMs: number }) => {
      // Stop any existing preview first
      clearTimer();

      // Calculate hook position (center of track ± 7.5 seconds)
      const hookStart = Math.floor(track.durationMs / 2 - HOOK_OFFSET_MS);

      // Start playback at hook position
      play.mutate(
        {
          uris: [track.uri],
          positionMs: Math.max(0, hookStart), // Ensure non-negative
        },
        {
          onSuccess: () => {
            // Update state on successful playback start
            setPreviewTrackId(track.id);
            setIsPlaying(true);

            // Set auto-stop timer
            timerRef.current = window.setTimeout(() => {
              stopPreview();
            }, PREVIEW_DURATION_MS);
          },
          onError: (error: any) => {
            console.error('Failed to start preview:', error);
            options?.onError?.(error);
            setPreviewTrackId(null);
            setIsPlaying(false);
          },
        }
      );
    },
    [clearTimer, play, stopPreview, options]
  );

  // Cleanup on unmount - stop playback and clear timer
  useEffect(() => {
    return () => {
      clearTimer();
      // Don't call stopPreview here as it would trigger pause mutation during unmount
      // Just clean up the timer
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [clearTimer]);

  return {
    playPreview,
    stopPreview,
    previewTrackId,
    isPlaying,
    isPending: play.isPending, // Loading state from mutation
  };
}
